import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Chip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import intl from 'components/i18n/ReactIntlWrapper';
import { useApi } from 'api/ApiProvider';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { TimeOffType } from 'axiosApi/models/time-off-type';
import Modal from 'components/common/Modal';
import { handleAPIError } from 'common/errorHandler';
import ErrorSummary from 'components/common/ErrorSummary';

interface AddTimeOffAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  employeeRegimeId: number;
  onSuccess?: () => void;
}

interface FormData {
  idEmployeeTimeOffRegime: number | null;
  idEmployeeRegimeTimeOffType: number | null;
  name: string;
  validFrom: Date | null;
  validUntil: Date | null;
  availableStock: string;
}

const normalizeDateOnlyForApi = (date: Date | null): Date | undefined => {
  if (!date) return undefined;
  const normalized = new Date(date);
  normalized.setHours(12, 0, 0, 0);
  return normalized;
};

const exceedsOneYearRange = (start: Date | null, end: Date | null): boolean => {
  if (!start || !end) return false;
  const maxEndDate = new Date(start);
  maxEndDate.setFullYear(maxEndDate.getFullYear() + 1);
  return end >= maxEndDate;
};

const AddTimeOffAssignmentModal: React.FC<AddTimeOffAssignmentModalProps> = ({
  isOpen,
  onClose,
  employeeRegimeId,
  onSuccess
}) => {
  const api = useApi();
  const toast = useToastMessageQueue();

  const [formData, setFormData] = useState<FormData>({
    idEmployeeTimeOffRegime: employeeRegimeId,
    idEmployeeRegimeTimeOffType: null,
    name: '',
    validFrom: null,
    validUntil: null,
    availableStock: ''
  });

  const [availableTimeOffTypes, setAvailableTimeOffTypes] = useState<TimeOffType[]>([]);
  const [selectedTimeOffType, setSelectedTimeOffType] = useState<TimeOffType | null>(null);
  const [isLoadingTypes, setIsLoadingTypes] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<any>({});

  useEffect(() => {
    if (employeeRegimeId) {
      fetchTimeOffTypes();
    } else {
      setAvailableTimeOffTypes([]);
      setSelectedTimeOffType(null);
      setFormData(prev => ({
        ...prev,
        idEmployeeRegimeTimeOffType: null
      }));
    }
  }, [employeeRegimeId]);

  const fetchTimeOffTypes = async () => {
    setIsLoadingTypes(true);
    try {
      const response = await api.timeoffApi.apiVversionListEmployeeTimeOffTypesEmployeeRegimeIdGet(employeeRegimeId, "1");
      if (response.status === 200 && response.data?.data) {
        const cleanData = response.data.data.queryResult.map((type) => ({
          ...type,
          name: type.label
        }));
        setAvailableTimeOffTypes(cleanData);
      } else {
        setAvailableTimeOffTypes([]);
      }
    } catch (error) {
      console.error('Error fetching time off types:', error);
      toast.error({
        header: intl.get(error.response?.data?.errors[0]?.errorLabelKey) ,
        message: intl.get(error.response?.data?.errors[0]?.errorLabelKey) 
      });
      handleAPIError(error.response.data.errors[0], toast, errors);
      setErrors({ ...errors });
      setAvailableTimeOffTypes([]);
    } finally {
      setIsLoadingTypes(false);
    }
  };

  const resetForm = () => {
    setFormData({
      idEmployeeTimeOffRegime: employeeRegimeId,
      idEmployeeRegimeTimeOffType: null,
      name: '',
      validFrom: null,
      validUntil: null,
      availableStock: ''
    });
    setSelectedTimeOffType(null);
    setErrors({});
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.idEmployeeTimeOffRegime) {
      newErrors.idEmployeeTimeOffRegime = intl.get('validation.required');
    }

    if (!formData.idEmployeeRegimeTimeOffType) {
      newErrors.idEmployeeRegimeTimeOffType = intl.get('validation.required');
    }

    if (!formData.name.trim()) {
      newErrors.name = intl.get('validation.required');
    }

    if (!formData.validFrom) {
      newErrors.validFrom = intl.get('validation.required');
    }

    if (!formData.validUntil) {
      newErrors.validUntil = intl.get('validation.required');
    }

    if (formData.validFrom && formData.validUntil && formData.validFrom > formData.validUntil) {
      newErrors.validUntil = intl.get('validation.dateRange.endAfterStart');
    } else if (exceedsOneYearRange(formData.validFrom, formData.validUntil)) {
      newErrors.validUntil = intl.get('validation.dateRange.oneYearMax')  ;
      newErrors.validFrom = intl.get('validation.dateRange.oneYearMax');
    }

    if (!formData.availableStock.trim()) {
      newErrors.availableStock = intl.get('validation.required');
    } else if (isNaN(Number(formData.availableStock)) || Number(formData.availableStock) < 0) {
      newErrors.availableStock = intl.get('validation.number.positive');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormValid = (): boolean => {
    const validUntilMinusOneDay = formData.validUntil 
      ? new Date(formData.validUntil.getTime() + 24 * 60 * 60 * 1000) 
      : null;
    
    return !!(
      formData.idEmployeeTimeOffRegime &&
      formData.idEmployeeRegimeTimeOffType &&
      formData.name.trim() &&
      formData.validFrom &&
      formData.validUntil &&
      !exceedsOneYearRange(formData.validFrom, formData.validUntil) &&
      formData.availableStock.trim() &&
      validUntilMinusOneDay &&
      formData.validFrom < validUntilMinusOneDay  
    );
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const createCommand = {
        idEmployeeTimeOffRegime: employeeRegimeId,
        idEmployeeRegimeTimeOffType: formData.idEmployeeRegimeTimeOffType,
        name: formData.name,
        validFrom: normalizeDateOnlyForApi(formData.validFrom),
        validUntil: normalizeDateOnlyForApi(formData.validUntil),
        availableStock: Number(formData.availableStock)
      };

      const response = await api.timeoffApi.apiVversionEmployeeTimeOffStockPost(
        "1",
        createCommand
      );

      if (response.status === 200 || response.status === 201) {
        toast.success({
          message: intl.get('timeOff.assignment.toast.success')
        });
        onSuccess?.();
        onClose();
      } else {
        throw new Error(intl.get('timeOff.assignment.error.failedToCreate'));
      }
    } catch (error) {
      console.error('Error creating assignment:', error);
      toast.error({
        message: intl.get(error.response?.data?.errors[0]?.errorLabelKey) 
      });
      handleAPIError(error.response.data.errors[0], toast, errors);
      setErrors({ ...errors });  
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} closeModal={handleClose} size="medium">
      <ErrorSummary errors={errors} showEverything={true} mode="warning" />
      <Box sx={{ position: 'relative', pt: 2 }}>
        <Box sx={{ mb: 3, pr: 5 }}>
          <Typography variant="h5" component="h2" fontWeight="bold">
{intl.get('timeOff.assignment.title')}
          </Typography>
        </Box>

          <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth error={!!errors.idEmployeeRegimeTimeOffType}>
                  <InputLabel required>{intl.get('timeOff.assignment.employeeRegimeTimeOffType')}</InputLabel>
                  <Select
                    value={formData.idEmployeeRegimeTimeOffType || ''}
                    label={intl.get('timeOff.assignment.employeeRegimeTimeOffType')}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      idEmployeeRegimeTimeOffType: Number(e.target.value) || null
                    }))}
                    disabled={isSubmitting || isLoadingTypes || !employeeRegimeId}
                  >
                    {isLoadingTypes ? (
                      <MenuItem disabled>
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                      </MenuItem>
                    ) : (
                      availableTimeOffTypes.map((type) => (
                        <MenuItem key={type.id} value={type.id}>
                          {type.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {errors.idEmployeeRegimeTimeOffType && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                      {errors.idEmployeeRegimeTimeOffType}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label={intl.get('timeOff.assignment.name')}
                  required
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  error={!!errors.name}
                  helperText={errors.name}
                  disabled={isSubmitting}
                  placeholder={intl.get('timeOff.assignment.namePlaceholder')}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
{intl.get('timeOff.assignment.expiration')} <span style={{ color: 'red' }}>*</span>
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <DatePicker
                      label={intl.get('timeOff.assignment.dateFrom')}
                      value={formData.validFrom}
                      onChange={(date) => setFormData(prev => ({ ...prev, validFrom: date }))}
                      disabled={isSubmitting}
                      slotProps={{
                        textField: {
                          size: 'small',
                          error: !!errors.validFrom,
                          helperText: !exceedsOneYearRange(formData.validFrom, formData.validUntil) && errors.validFrom,
                          sx: { flex: 1 }
                        }
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">-</Typography>
                    <DatePicker
                      label={intl.get('timeOff.assignment.dateUntil')}
                      value={formData.validUntil}
                      onChange={(date) => setFormData(prev => ({ ...prev, validUntil: date }))}
                      disabled={isSubmitting}
                      slotProps={{
                        textField: {
                          size: 'small',
                          error: !!errors.validUntil,
                          helperText: !exceedsOneYearRange(formData.validFrom, formData.validUntil) && errors.validUntil,
                          sx: { flex: 1 }
                        }
                      }}
                    />
                  </Box>
                </LocalizationProvider>
                {!!errors.validFrom && exceedsOneYearRange(formData.validFrom, formData.validUntil) && (
                <Typography variant="caption" color="error">
                  {intl.get('validation.dateRange.oneYearMax')}
                </Typography>
              )}
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label={intl.get('timeOff.assignment.availableStock')}
                  required
                  type="number"
                  value={formData.availableStock}
                  onChange={(e) => setFormData(prev => ({ ...prev, availableStock: e.target.value }))}
                  error={!!errors.availableStock}
                  helperText={errors.availableStock}
                  disabled={isSubmitting}
                  inputProps={{ min: 0, step: 1 }}
                />
              </Grid>

              {selectedTimeOffType && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    {intl.get('timeOff.assignment.perks')}
                  </Typography>
                  {(() => {
                    const perks = [];
                    if (selectedTimeOffType.isBillable) {
                      perks.push(intl.get('timeOff.perks.billable'));
                    }
                    if (selectedTimeOffType.isCumulative) {
                      perks.push(intl.get('timeOff.perks.cumulative'));
                    }
                    if (selectedTimeOffType.requiresSpecialApproval) {
                      perks.push(intl.get('timeOff.perks.specialApproval'));
                    }
                    if (selectedTimeOffType.allowNegativeBalance) {
                      perks.push(intl.get('timeOff.perks.negativeBalance'));
                    }

                    return perks.length > 0 ? (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {perks.map((perk, index) => (
                          <Chip
                            key={index}
                            label={perk}
                            variant="filled"
                            color="primary"
                            size="small"
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        {intl.get('timeOff.assignment.noPerksAssociated')}
                      </Typography>
                    );
                  })()}
                </Grid>
              )}

            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
              <Button
                variant="outlined"
                onClick={handleClose}
                disabled={isSubmitting}
              >
{intl.get('common.cancel')}
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={isSubmitting || !isFormValid()}
                startIcon={isSubmitting ? <CircularProgress size={16} /> : undefined}
              >
{intl.get('common.confirm')}
              </Button>
            </Box>
          </Box>
      </Box>
    </Modal>
  );
};

export default AddTimeOffAssignmentModal;
